import { Tooltip, TooltipContent, TooltipTrigger } from "@istari-frontend/ui";
import { SquareArrowOutUpRight } from "lucide-react";
import { Link } from "react-router";

import { cn } from "~/utils/cn";

interface ChatArtifactCardProps {
  name: string;
  // Short type badge, e.g. "HTML", "PDF", "MD".
  kind: string;
  // Secondary line, e.g. "v14 · 184 KB".
  meta?: string;
  // Opens the artifact in the page's canvas pane.
  onOpen?: () => void;
  // Falls back to a link to the resource in the library when there is no canvas.
  href?: string;
  className?: string;
}

// The artifact attachment card shown under an assistant turn (e.g. a generated
// "Readme.html"): a type badge, the file name + metadata, and an open-out glyph. The
// whole card is the tap target, routing to the canvas pane (or the library when no
// canvas is present); without either it is a plain, inert card.
export function ChatArtifactCard({ name, kind, meta, onOpen, href, className }: ChatArtifactCardProps) {
  const hint = "Open";
  const body = (
    <>
      <div className="bg-muted text-muted-foreground flex size-10 shrink-0 items-center justify-center rounded-md text-[10px] font-semibold tracking-wide uppercase">
        {kind}
      </div>
      <div className="min-w-0 flex-1 text-left">
        <div className="text-foreground truncate text-sm font-medium">{name}</div>
        {meta ? <div className="text-muted-foreground truncate text-xs">{meta}</div> : null}
      </div>
      {onOpen || href ? (
        <span className="border-input bg-background text-foreground flex size-7 shrink-0 items-center justify-center rounded-md border shadow-xs">
          <SquareArrowOutUpRight className="size-4" aria-hidden="true" />
        </span>
      ) : null}
    </>
  );
  const cardClassName = cn(
    "border-border bg-card flex w-full max-w-full items-center gap-3 rounded-lg border p-2.5",
    (onOpen || href) &&
      "hover:bg-accent/50 focus-visible:ring-ring/50 transition-colors outline-none focus-visible:ring-[3px]",
    className,
  );

  if (onOpen) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <button type="button" onClick={onOpen} aria-label={`Open ${name}`} className={cardClassName}>
            {body}
          </button>
        </TooltipTrigger>
        <TooltipContent side="top">{hint}</TooltipContent>
      </Tooltip>
    );
  }
  if (href) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <Link to={href} aria-label={`Open ${name}`} className={cardClassName}>
            {body}
          </Link>
        </TooltipTrigger>
        <TooltipContent side="top">{hint}</TooltipContent>
      </Tooltip>
    );
  }
  return <div className={cardClassName}>{body}</div>;
}
